<template>
  <v-container fluid class="mt-16 px-6">
    <div>
      <v-card flat>
        <v-card-title class="d-flex">
          <h2 v-if="!isPhone">Invoices</h2>

          <v-spacer></v-spacer>
          <v-btn small @click="print_unpaid_invoices"
            >Print Unpaid Invoices</v-btn
          >
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                small
                class="ml-2"
                color="info"
                :loading="exporting"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left small>mdi-code-json</v-icon>Export JSON
              </v-btn>
            </template>
            <v-list dense>
              <v-subheader>Export scope</v-subheader>
              <v-list-item @click="exportInvoices('All')">
                <v-list-item-title>All Invoices</v-list-item-title>
              </v-list-item>
              <v-list-item @click="exportInvoices('Unpaid')">
                <v-list-item-title>Unpaid Invoices</v-list-item-title>
              </v-list-item>
              <v-list-item @click="exportInvoices('Paid')">
                <v-list-item-title>Paid Invoices</v-list-item-title>
              </v-list-item>
              <v-list-item @click="exportInvoices('Deleted')">
                <v-list-item-title>Deleted Invoices</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn small @click="openDownPaymentModal" class="ml-2" color="orange"
            >Down Payments</v-btn
          >
          <v-btn
            v-if="!isPhone"
            small
            @click="openAddInvoiceModal"
            class="ml-2"
            color="green"
            >Add Invoice</v-btn
          >
          <v-btn
            color=""
            class=""
            icon
            x-large
            @click="refresh"
            :loading="loading"
            ><v-icon>mdi-refresh</v-icon></v-btn
          >
        </v-card-title>

        <v-tabs v-model="activeTab">
          <v-tab>
            <v-icon left small>mdi-receipt-text</v-icon>
            <span class="text-none">All Invoices</span>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-receipt-text-check</v-icon>
            <span class="text-none">Paid Invoices</span>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-receipt-text-remove</v-icon>
            <span class="text-none">Deleted Invoices</span>
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <v-card-text>
              <div>
                <div class="d-flex align-center">
                  <v-text-field
                    label="Search by ID, invoice #, customer, or purpose"
                    prepend-icon="mdi-magnify"
                    v-model="search"
                    clearable
                  ></v-text-field>
                  <v-select
                    v-model="sortBy"
                    :items="sortOptions"
                    label="Sort by"
                    class="ml-4"
                    style="max-width: 180px"
                    dense
                    hide-details
                  ></v-select>
                  <v-btn
                    icon
                    class="ml-2"
                    :title="sortDesc ? 'Descending' : 'Ascending'"
                    @click="sortDesc = !sortDesc"
                  >
                    <v-icon>{{
                      sortDesc ? "mdi-sort-descending" : "mdi-sort-ascending"
                    }}</v-icon>
                  </v-btn>
                </div>
                <!-- Mobile: card list -->
                <div v-if="isPhone" class="mt-2">
                  <mobile-record-card
                    v-for="item in $mobileSearch(all_invoices, search)"
                    :key="item.id"
                    icon="mdi-receipt-text"
                    :title="item.invoice_code"
                    :subtitle="item.customer ? item.customer.customer_name : ''"
                    :fields="[
                      {
                        label: 'Total',
                        value: '₱' + (item.total_amount || 0).toLocaleString(),
                      },
                      {
                        label: 'Balance',
                        value: '₱' + (item.balance || 0).toLocaleString(),
                        class: item.balance > 0 ? 'red--text' : 'green--text',
                      },
                      { label: 'Purpose', value: item.purpose },
                    ]"
                  >
                    <template #chip>
                      <v-chip
                        x-small
                        :color="
                          item.invoice_status == 'Paid' ? 'green' : 'red'
                        "
                        dark
                        >{{ item.invoice_status }}</v-chip
                      >
                    </template>
                    <template #actions>
                      <v-btn
                        icon
                        color="primary"
                        @click="enablePayablesModal(item)"
                        ><v-icon>mdi-logout-variant</v-icon></v-btn
                      >
                      <v-btn
                        v-if="can('invoices.add_payment')"
                        icon
                        color="amber darken-2"
                        @click="open_payment(item)"
                        ><v-icon>mdi-cash-multiple</v-icon></v-btn
                      >
                      <v-btn icon color="success" @click="print_modal(item)"
                        ><v-icon>mdi-printer</v-icon></v-btn
                      >
                      <v-btn
                        v-if="can('invoices.delete')"
                        icon
                        color="red"
                        :loading="loading"
                        @click="del_invoice(item)"
                        ><v-icon>mdi-delete</v-icon></v-btn
                      >
                    </template>
                  </mobile-record-card>
                  <div
                    v-if="!$mobileSearch(all_invoices, search).length"
                    class="text-center text--secondary py-8"
                  >
                    No invoices found.
                  </div>
                </div>
                <!-- Desktop: data table -->
                <v-data-table
                  v-else
                  :items="all_invoices"
                  :headers="headers"
                  :search="search"
                  :custom-filter="invoiceSearchFilter"
                  :sort-by.sync="sortBy"
                  :sort-desc.sync="sortDesc"
                  :loading="loading"
                >
                  <template v-slot:item.invoice_code="{ item }">
                    <div class="d-flex align-center">
                      {{ item.invoice_code }}
                    </div>
                  </template>
                  <template v-slot:item.customer.customer_name="{ item }">
                    <span
                      :class="
                        item.customer && item.customer.is_government == 1
                          ? 'blue--text'
                          : ''
                      "
                      >{{
                        item.customer ? item.customer.customer_name : ""
                      }}</span
                    >
                  </template>

                  <template v-slot:item.action="{ item }">
                    <div class="d-flex">
                      <v-btn
                        icon
                        @click="enablePayablesModal(item)"
                        color="primary"
                        class=""
                        ><v-icon class="">mdi-logout-variant</v-icon>
                      </v-btn>

                      <v-btn
                        v-if="can('invoices.add_payment')"
                        @click="open_payment(item)"
                        color="yellow"
                        icon
                        ><v-icon>mdi-cash-multiple</v-icon></v-btn
                      >
                      <v-btn
                        @click="print_modal(item)"
                        small
                        icon
                        color="success"
                        class="mt-1"
                        ><v-icon>mdi-printer</v-icon></v-btn
                      >

                      <v-btn
                        v-if="can('invoices.delete')"
                        icon
                        @click="del_invoice(item)"
                        color="red"
                        class="mr-1"
                        :loading="loading"
                        ><v-icon class="mr-2">mdi-delete</v-icon></v-btn
                      >
                    </div>
                  </template>
                  <template v-slot:item.balance="{ item }">
                    <span>{{ (item.balance || 0).toLocaleString() }}</span>
                  </template>
                  <template v-slot:item.total_amount="{ item }">
                    <span>{{ (item.total_amount || 0).toLocaleString() }}</span>
                  </template>
                  <template v-slot:item.invoice_status="{ item }">
                    <span
                      :class="
                        item.invoice_status == 'Paid'
                          ? 'green--text'
                          : 'red--text'
                      "
                      >{{ item.invoice_status }}</span
                    >
                  </template>
                  <template v-slot:item.is_invoice="{ item }">
                    <v-chip
                      color="blue"
                      class="white--text"
                      v-if="item.is_invoice"
                      >Invoice</v-chip
                    >
                    <v-chip color="red" class="white--text" v-else>Bill</v-chip>
                  </template>
                  <template v-slot:item.warranty_policy.name="{ item }">
                    <v-chip
                      v-if="item.warranty_policy"
                      small
                      :color="item.warranty_policy.color || 'blue'"
                      class="white--text"
                      >{{ item.warranty_policy.name }}</v-chip
                    >
                    <span v-else class="grey--text">N/A</span>
                  </template>
                  <template v-slot:item.warranty_remaining="{ item }">
                    <span
                      v-if="item.warranty_policy"
                      :class="getWarrantyRemainingClass(item)"
                    >
                      {{ getWarrantyRemaining(item) }}
                    </span>
                    <span v-else class="grey--text">N/A</span>
                  </template>
                  <template v-slot:item.purpose="{ item }">
                    <div
                      v-if="item.purpose"
                      class="purpose-cell"
                      :title="item.purpose"
                    >
                      {{ item.purpose }}
                    </div>
                    <span v-else class="grey--text">—</span>
                  </template>
                </v-data-table>
              </div>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <paid-invoices-table />
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <deleted-invoices-table />
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>
    <!-- lazy loaded -->
    <add-payment
      v-if="enable_add_payment_modal"
      :invoice="selected_invoice"
      @close="enable_add_payment_modal = false"
    />
    <print-modal-card
      v-if="show_print_modal"
      :item="selected_invoice"
      @close="show_print_modal = false"
    />
    <payables-modal
      v-if="enable_payables_modal"
      :invoice="selected_invoice"
      @close="enable_payables_modal = false"
    />

    <!-- Add Invoice Card Component -->
    <add-invoice-card ref="addInvoiceCard" />

    <!-- Down Payments Modal Component -->
    <down-payments-modal ref="downPaymentsModal" />

    <!-- Mobile: floating Add Invoice -->
    <v-btn
      v-if="isPhone"
      fab
      fixed
      bottom
      right
      color="green"
      dark
      class="mobile-fab"
      @click="openAddInvoiceModal"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>
  </v-container>
</template>

<script>
// import { defineAsyncComponent } from "vue";
import axios from "axios";
import { mapGetters, mapActions } from "vuex";
import {
  invoiceSearchFilter,
  invoiceSortOptions,
} from "../utils/invoiceSearchFilter";

export default {
  components: {
    AddInvoiceCard: () => import("../components/Invoice/addInvoiceCard.vue"),
    payablesModal: () => import("../components/Payment/payablesModal.vue"),
    addPayment: () => import("../components/Payment/addPaymentModal.vue"),
    PrintModalCard: () => import("../components/Invoice/printModalCard.vue"),
    PaidInvoicesTable: () =>
      import("../components/Invoice/paidInvoicesTable.vue"),
    DeletedInvoicesTable: () =>
      import("../components/Invoice/deletedInvoicesTable.vue"),
    DownPaymentsModal: () =>
      import("../components/Invoice/downPaymentsModal.vue"),
  },
  data() {
    return {
      enable_payables_modal: false,
      activeTab: 0,
      show_print_modal: false,
      selected_invoice: [],
      enable_add_payment_modal: false,
      ready: false,
      search: "",
      loading: false,
      exporting: false,
      sortBy: "id",
      sortDesc: true,
      sortOptions: invoiceSortOptions,
      headers: [
        { text: "Action", value: "action", width: "80" },
        { text: "ID", value: "id" },
        { text: "Invoice #", value: "invoice_code" },
        { text: "Account Name", value: "customer.customer_name" },
        { text: "Invoice Date", value: "invoice_date" },
        { text: "Invoice Status", value: "invoice_status" },
        { text: "Balance", value: "balance" },
        { text: "Cheque Amount", value: "cheque_amount" },

        { text: "Total Amount", value: "total_amount" },

        { text: "Warranty Policy", value: "warranty_policy.name" },
        { text: "Warranty Remaining", value: "warranty_remaining" },
        { text: "Purpose", value: "purpose", width: 240 },
      ],
    };
  },
  computed: {
    ...mapGetters({
      default_server_url: "auth/default_server_url",
      all_invoices: "invoice/all_invoices",
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
  },
  methods: {
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    invoiceSearchFilter,
    getWarrantyExpiryDays(invoiceDate, value, unit) {
      const date = new Date(invoiceDate);
      if (isNaN(date.getTime())) return null;
      if (unit === "months") {
        date.setMonth(date.getMonth() + value);
      } else if (unit === "days") {
        date.setDate(date.getDate() + value);
      } else if (unit === "years") {
        date.setFullYear(date.getFullYear() + value);
      }
      const now = new Date();
      const diffMs = date.getTime() - now.getTime();
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    },
    getWarrantyRemaining(item) {
      if (!item.warranty_policy) return "N/A";
      const wp = item.warranty_policy;
      const entries = [
        {
          label: "Major Parts",
          days: this.getWarrantyExpiryDays(
            item.invoice_date,
            wp.major_parts_warranty_value,
            wp.major_parts_warranty_unit
          ),
        },
        {
          label: "Minor Parts",
          days: this.getWarrantyExpiryDays(
            item.invoice_date,
            wp.minor_parts_warranty_value,
            wp.minor_parts_warranty_unit
          ),
        },
        {
          label: "Service",
          days: this.getWarrantyExpiryDays(
            item.invoice_date,
            wp.service_warranty_value,
            wp.service_warranty_unit
          ),
        },
      ];
      const maxEntry = entries.reduce((a, b) => (a.days > b.days ? a : b));
      if (maxEntry.days === null) return "N/A";
      if (maxEntry.days <= 0) return "Expired";
      return maxEntry.days + " day" + (maxEntry.days !== 1 ? "s" : "");
    },
    getWarrantyRemainingClass(item) {
      if (!item.warranty_policy) return "grey--text";
      const wp = item.warranty_policy;
      const days = [
        this.getWarrantyExpiryDays(
          item.invoice_date,
          wp.major_parts_warranty_value,
          wp.major_parts_warranty_unit
        ),
        this.getWarrantyExpiryDays(
          item.invoice_date,
          wp.minor_parts_warranty_value,
          wp.minor_parts_warranty_unit
        ),
        this.getWarrantyExpiryDays(
          item.invoice_date,
          wp.service_warranty_value,
          wp.service_warranty_unit
        ),
      ];
      const maxDays = Math.max(...days.filter((d) => d !== null));
      if (maxDays <= 0) return "red--text";
      if (maxDays <= 30) return "orange--text";
      return "green--text";
    },
    openAddInvoiceModal() {
      this.$refs.addInvoiceCard.dialog = true;
    },
    openDownPaymentModal() {
      this.$refs.downPaymentsModal.dialog = true;
    },
    // Triggered by the global Quick Actions palette via ?quick= query param.
    // The target modals are lazy-loaded, so retry until their refs resolve.
    handleQuickAction(action, attempt = 0) {
      const refName =
        action === "add-invoice"
          ? "addInvoiceCard"
          : action === "add-downpayment"
          ? "downPaymentsModal"
          : null;
      if (!refName) return;
      if (!this.$refs[refName]) {
        if (attempt < 20) {
          setTimeout(() => this.handleQuickAction(action, attempt + 1), 100);
        }
        return;
      }
      if (action === "add-invoice") this.openAddInvoiceModal();
      else if (action === "add-downpayment") this.openDownPaymentModal();
      // Clear the query so the same action can be triggered again later.
      const query = { ...this.$route.query };
      delete query.quick;
      this.$router.replace({ query }).catch(() => {});
    },
    async fetchDetail(invoice) {
      try {
        return await this.get_invoice_detail(invoice.id);
      } catch (e) {
        this.$alert({
          title: "Error",
          message: "Failed to load invoice details",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return null;
      }
    },
    async enablePayablesModal(invoice) {
      this.loading = true;
      const detail = await this.fetchDetail(invoice);
      this.loading = false;
      if (!detail) return;
      this.selected_invoice = detail;
      this.enable_payables_modal = true;
    },
    async print_modal(item) {
      this.loading = true;
      const detail = await this.fetchDetail(item);
      this.loading = false;
      if (!detail) return;
      this.selected_invoice = detail;
      this.show_print_modal = true;
    },
    print_unpaid_invoices() {
      let url =
        this.default_server_url +
        "/print_unpaid_invoices/" +
        this.user.branch_id;
      window.open(url);
    },
    async open_payment(item) {
      this.loading = true;
      const detail = await this.fetchDetail(item);
      this.loading = false;
      if (!detail) return;
      this.selected_invoice = detail;
      this.enable_add_payment_modal = true;
    },
    async del_invoice(item) {
      this.loading = true;
      let y = await this.$confirm({
        title: "Delete Invoice",
        message: "Are you sure you want to delete this invoice?",
        color: "red",
        icon: "mdi-delete",
      });
      if (y) {
        try {
          await this.delete_invoice(item);
          this.$alert({
            title: "Success",
            message: "Successfully deleted an invoice",
            color: "success",
            icon: "mdi-check-circle",
          });
        } catch (e) {
          this.$alert({
            title: "Error",
            message: e.response ? e.response.data.error : e.message,
            color: "error",
            icon: "mdi-alert-circle",
          });
        }
        this.loading = false;
      } else {
        this.loading = false;
      }
    },
    refresh() {
      this.loading = true;
      this.get_all_invoices().then(() => {
        this.loading = false;
      });

      return;
    },
    // Fetch every paid invoice (the /paid_invoices endpoint is paginated, so
    // ask for a single large page instead of the table's current page only).
    async fetchAllPaidInvoices() {
      const response = await axios.get("/paid_invoices", {
        params: {
          page: 1,
          per_page: 100000,
          search: "",
          sort_by: "id",
          sort_dir: "desc",
        },
      });
      return (response.data && response.data.data) || [];
    },
    mapInvoiceForExport(inv) {
      return {
        id: inv.id,
        invoice_code: inv.invoice_code || null,
        customer_name: inv.customer ? inv.customer.customer_name : null,
        is_government: inv.customer ? !!inv.customer.is_government : null,
        invoice_date: inv.invoice_date || null,
        invoice_status: inv.invoice_status || null,
        balance: Number(inv.balance || 0),
        cheque_amount:
          inv.cheque_amount != null ? Number(inv.cheque_amount) : null,
        total_amount: Number(inv.total_amount || 0),
        warranty_policy: inv.warranty_policy ? inv.warranty_policy.name : null,
        purpose: inv.purpose || null,
        type: inv.is_invoice ? "Invoice" : "Bill",
        deleted_at: inv.deleted_at || null,
      };
    },
    async exportInvoices(scope) {
      this.exporting = true;
      try {
        let records = [];
        if (scope === "Paid") {
          records = await this.fetchAllPaidInvoices();
        } else if (scope === "Deleted") {
          const response = await axios.get("/deleted_invoices");
          records = response.data || [];
        } else if (scope === "Unpaid") {
          const response = await axios.get("/unpaid_invoices");
          records = response.data || [];
        } else {
          // All = unpaid/partial (/all_invoices excludes paid server-side) + paid
          const [unpaidRes, paid] = await Promise.all([
            axios.get("/all_invoices"),
            this.fetchAllPaidInvoices(),
          ]);
          records = [...(unpaidRes.data || []), ...paid];
        }

        if (!records.length) {
          this.$alert({
            title: "No Records",
            message: `There are no ${scope.toLowerCase()} invoices to export.`,
            color: "warning",
            icon: "mdi-alert",
          });
          return;
        }

        const data = records.map((inv) => this.mapInvoiceForExport(inv));
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoices-${scope.toLowerCase()}-${
          new Date().toISOString().split("T")[0]
        }.json`;
        link.click();
        URL.revokeObjectURL(url);

        this.$alert({
          title: "Export Complete",
          message: `Exported ${
            data.length
          } ${scope.toLowerCase()} invoice(s) to JSON.`,
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (e) {
        this.$alert({
          title: "Export Failed",
          message: e.response
            ? e.response.data.error || e.response.data.message
            : e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.exporting = false;
      }
    },
    ...mapActions({
      get_all_invoices: "invoice/get_all_invoices",
      get_invoice_detail: "invoice/get_invoice_detail",
      delete_invoice: "invoice/delete_invoice",
    }),
  },
  watch: {
    // React to Quick Actions (Ctrl/Cmd + S) navigation while already on this page.
    "$route.query.quick": {
      immediate: true,
      handler(val) {
        if (val) this.$nextTick(() => this.handleQuickAction(val));
      },
    },
  },
  async created() {
    this.loading = true;
    if (this.all_invoices.length <= 0) {
      await this.get_all_invoices();
    }
    this.loading = false;
  },
};
</script>

<style scoped>
/* Keep the Purpose column to a single line with an ellipsis so long
   purposes don't stretch the row height. Full text shows on hover (title). */
.purpose-cell {
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
